import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { TASK_MASTER_LIST } from "../../routes";
import { useTaskMaster } from "./Hooks/useTaskMaster";
import { Spinner, ScrollableDropdown } from "../../Components/Common";
import { PROJECT_TYPE, CATEGORY_TYPE } from "./constants";

const CreateTaskMaster = () => {
  const {
    taskMasters,
    fetchTaskMasters,
    createTaskMaster,
    loading,
    createSubTask,
  } = useTaskMaster({ fetch: true });

  const [selectedType, setSelectedType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [activeDropdown, setActiveDropdown] = useState(null);

  const navigate = useNavigate();

  const typeRef = useRef(null);
  const categoryRef = useRef(null);
  const parentRef = useRef(null);

  const handleClickOutside = (e) => {
    if (
      typeRef.current?.contains(e.target) ||
      categoryRef.current?.contains(e.target) ||
      parentRef.current?.contains(e.target)
    ) {
      return;
    }
    setActiveDropdown(null);
  };

  useEffect(() => {
    fetchTaskMasters(); // fetch parent tasks on page load
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initialValues = {
    isSubTask: false,
    title: "",
    description: "",
    project_type: "",
    project_category: "",
    weight: 1,
    order: "",
    parent: null, // optional FK
  };

  const validationSchema = Yup.object({
    isSubTask: Yup.boolean(),
    title: Yup.string().required("Task title is required"),
    description: Yup.string(),
    project_type: Yup.string().required("Project type is required"),
    project_category: Yup.string().required("Project category is required"),
    weight: Yup.number()
      .required("Weight is required")
      .min(1, "Weight cannot be less than 1"),
    order: Yup.number()
      .required("Order is required")
      .min(1, "Order must be greater than or equal to 1"),
    parent: Yup.string().nullable(),
  });

  const handleSubmit = async (data) => {
    const payload = {
      title: data.title,
      description: data.description,
      project_type: data.project_type,
      project_category: data.project_category,
      weight: data.weight,
      order: data.order,
      super_task: data.parent === "NO_PARENT" ? null : data.parent, // backend expects super_task
    };

    let result = null

      if (data.isSubTask) {
      result = await createSubTask(payload); // call Sub-task API
      } else {
      result = await createTaskMaster(payload); // call Master task API
      }

    if(result){
      navigate(TASK_MASTER_LIST);
    }

  };

  const filteredParents = taskMasters
    .filter(
      (t) =>
        (!selectedType || t.project_type === selectedType) &&
        (!selectedCategory || t.project_category === selectedCategory)
    )
    .map((t) => ({ label: t.title, value: t.id }));

  return (
    <div className="mx-2 my-1 md:my-2">
      {/* Header */}
      <div className="flex flex-row items-center justify-between gap-4 mx-2 my-6 md:mt-5 mb-2">
        <div className="flex justify-start gap-4 items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-primary text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={25} className="text-primary" />
          </button>
          <h1 className="text-xl md:text-2xl text-center text-secondary font-semibold">
            Create New Task
          </h1>
        </div>
      </div>

      {/* Form */}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={false}
      >
          <Form className="mx-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 py-2 mb-2 md:gap-6">
              {/* Task Details */}
              <div className="pr-0 md:pr-8 border-gray-200 lg:border-r">
                <div className="flex items-center justify-center gap-3">
                  <h2 className="text-xl font-semibold text-secondary mb-4">
                    Task Details
                  </h2>
                </div>

                <div className="space y-4">
                  {/* Title */}
                  <div className="flex flex-col gap-1 mb-4">
                    <label className="text-sm font-medium text-gray-700">
                      Title <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="title"
                      type="text"
                      placeholder="Enter task title"
                      className="border-b border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200"
                      disabled={loading}
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Description */}
                  <div className="flex flex-col gap-1 mb-4">
                    <label className="text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <Field
                      as="textarea"
                      rows="3"
                      name="description"
                      placeholder="Enter task description"
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                      disabled={loading}
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Order + Weight */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Order <sub className="text-red-500">*</sub>
                      </label>
                      <Field
                        name="order"
                        type="number"
                        placeholder="Enter order"
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                        min={1}
                      />
                      <ErrorMessage
                        name="order"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Weight <sub className="text-red-500">*</sub>
                      </label>
                      <Field
                        name="weight"
                        type="number"
                        placeholder="Enter weight"
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                        min={1}
                      />
                      <ErrorMessage
                        name="weight"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Parent Task Details */}
              <div className="pl-0 lg:pl-8 border-gray-200 mt-8 md:mt-0">
                <div className="flex items-center justify-center gap-3 md:mb-8 mb-4">
                  <h2 className="text-xl font-semibold text-secondary mb-3">
                    Parent Task Details
                  </h2>
                </div>

                <div className="space y-3">
                  {/* Is Sub-task Checkbox */}
                  <div className="flex items-center gap-2 md:mb-8 mb-5">
                    <Field
                      type="checkbox"
                      name="isSubTask"
                      id="isSubTask"
                      className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 "
                    />
                    <label
                      htmlFor="isSubTask"
                      className="text-sm font-medium text-gray-700"
                    >
                      Is this a Sub-task?
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-6 md:mb-8 mb-4">
                    {/* Project Type */}
                    <div className="flex flex-col gap-2 mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Project Type <sub className="text-red-500">*</sub>
                      </label>
                      <Field name="project_type">
                        {({ field, form }) => (
                          <ScrollableDropdown
                            ref={typeRef}
                            label="Select Project Type"
                            options={Object.entries(PROJECT_TYPE).map(
                              ([key, label]) => ({ label, value: key })
                            )}
                            value={field.value}
                            onChange={(val) => {
                              form.setFieldValue("project_type", val);
                              setSelectedType(val);
                            }}
                            isOpen={activeDropdown === "type"}
                            onToggle={() =>
                              setActiveDropdown((prev) =>
                                prev === "type" ? null : "type"
                              )
                            }
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="project_type"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Project Category */}
                    <div className="flex flex-col gap-2 mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Project Category <sub className="text-red-500">*</sub>
                      </label>
                      <Field name="project_category">
                        {({ field, form }) => (
                          <ScrollableDropdown
                            ref={categoryRef}
                            label="Select Project Category"
                            options={Object.entries(CATEGORY_TYPE).map(
                              ([key, label]) => ({ label, value: key })
                            )}
                            value={field.value}
                            onChange={(val) => {
                              form.setFieldValue("project_category", val);
                              setSelectedCategory(val);
                            }}
                            isOpen={activeDropdown === "category"}
                            onToggle={() =>
                              setActiveDropdown((prev) =>
                                prev === "category" ? null : "category"
                              )
                            }
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="project_category"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  {/* Parent Task Dropdown */}
                  <Field name="parent">
                    {({ field, form }) => (
                      <div className="flex flex-col gap-1">
                        <label
                          className={`text-sm font-medium ${
                            form.values.isSubTask
                              ? "text-gray-700"
                              : "text-gray-400"
                          } `}
                        >
                          Parent Task
                        </label>

                        <ScrollableDropdown
                          ref={parentRef}
                          label="Select parent task"
                          options={[...filteredParents]}
                          value={field.value}
                          onChange={(val) => form.setFieldValue("parent", val)}
                          isOpen={activeDropdown === "parent"}
                          onToggle={() =>
                            setActiveDropdown((prev) =>
                              prev === "parent" ? null : "parent"
                            )
                          }
                          disabled={!form.values.isSubTask}
                          className={`${
                            !form.values.isSubTask
                              ? "opacity-60 pointer-events-none"
                              : ""
                          }`}
                        />

                        <ErrorMessage
                          name="parent"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>

            {/* Save button */}
            <div className="flex justify-end gap-2 border-gray-200">
              <button
                type="submit"
                className="px-5 py-2 bg-primary text-white flex justify-center shadow hover:shadow-lg hover:bg-secondary active:scale-95 transition-all text-sm font-medium"
              disabled={loading}
              >
              {loading ? <Spinner /> : "Save Task"}
              </button>
            </div>
          </Form>
      </Formik>
    </div>
  );
};

export default CreateTaskMaster;
